import { Link, useLocation, useNavigate, matchPath } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useState } from "react";
import { ScanLine, ArrowLeft } from "lucide-react";
import { getMembershipConfig, normaliseTier } from "@/lib/membershipUtils";
import { useActiveWorkout } from "@/lib/ActiveWorkoutContext";
import WorkoutTimerPill from "@/components/dashboard/WorkoutTimerPill";
import { BRAND_LOGO } from "@/lib/brandAssets";
import MealScanner from "@/components/scanner/MealScanner";
import AthleteProfileModal from "./AthleteProfileModal";

export default function TopBar() {
  const { user } = useAuth();
  const { active: activeWorkout } = useActiveWorkout();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const cfg = getMembershipConfig(user?.membership_type);
  // When viewing a single library plan, the left-side meal-scan icon turns
  // into a back-to-library button so users have a one-tap exit from the
  // detail page (the swipe carousel makes the OS back-swipe gesture
  // unreliable on iOS). The Session Scheduler page (`/sessions`) gets the
  // same treatment so users can bail out of booking without hunting for the
  // bottom-nav home button.
  const onLibraryDetail = !!matchPath("/library/:id", location.pathname);
  const onSessionsPage = !!matchPath("/sessions", location.pathname);
  // Same back-arrow swap on the My Trainer page — one-tap exit out of the
  // profile view back to the home dashboard.
  const onMyTrainerPage = !!matchPath("/my-trainer", location.pathname);
  // Settings page back button is hoisted into the top nav and routes
  // back to /account (not /), so users land on the "My Account" page
  // they navigated FROM, not the home dashboard.
  const onSettingsPage = !!matchPath("/settings", location.pathname);
  // Vision meal scanner is gated to Elite Coaching tier (and admin/trainer
  // roles) per the same cost-control logic as text macro estimation, just
  // tighter because vision calls cost meaningfully more per request.
  const canScanMeal =
    normaliseTier(user?.membership_type) === "elite_coaching" ||
    user?.role === "admin" ||
    user?.role === "trainer";
  const initials = user?.full_name
    ? user.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : (user?.email?.[0] || "?").toUpperCase();

  const handleAccountClick = (e) => {
    e.preventDefault();
    setProfileOpen(true);
  };

  if (activeWorkout?.is_fullscreen) {
    return null;
  }

  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 shrink-0 border-b border-sidebar-border"
      style={{
        background: "#030A17",
        borderBottom: "1px solid #1E293B",
        // Push the bar content below the iOS status bar / Dynamic Island
        // when running inside the Capacitor shell. Resolves to 0 in the
        // regular web build so desktop / non-notched devices are unaffected.
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      <div className="flex items-center justify-between px-4 h-12 sm:h-13 max-w-7xl mx-auto relative w-full">
        {/* Left-corner button. On `/library/:id` this is a Back-to-Library
            arrow so the user can exit the swipe view in one tap; everywhere
            else it's the AI meal scanner (Elite tier only — vision LLM
            calls are the priciest in the app). */}
        <div className="w-8 h-8 flex items-center justify-center">
          {onLibraryDetail ? (
            <button
              type="button"
              onClick={() => navigate("/library")}
              aria-label="Back to Library"
              data-testid="topbar-back-to-library-btn"
              className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25 hover:text-sky-200 active:scale-95 transition flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : onSessionsPage ? (
            <button
              type="button"
              onClick={() => navigate("/")}
              aria-label="Back to Home"
              data-testid="topbar-back-from-sessions-btn"
              className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25 hover:text-sky-200 active:scale-95 transition flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : onMyTrainerPage ? (
            <button
              type="button"
              onClick={() => navigate("/")}
              aria-label="Back to Home"
              data-testid="topbar-back-from-my-trainer-btn"
              className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25 hover:text-sky-200 active:scale-95 transition flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : onSettingsPage ? (
            <button
              type="button"
              onClick={() => navigate("/account")}
              aria-label="Back to My Account"
              data-testid="topbar-back-from-settings-btn"
              className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25 hover:text-sky-200 active:scale-95 transition flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : canScanMeal ? (
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              aria-label="Scan a meal with AI"
              data-testid="topbar-meal-scanner-btn"
              className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-300 hover:bg-sky-500/25 hover:text-sky-200 hover:shadow-[0_0_18px_-4px_rgba(56,189,248,0.65)] active:scale-95 transition flex items-center justify-center"
            >
              <ScanLine className="w-4 h-4" />
            </button>
          ) : null}
        </div>
        <MealScanner open={scannerOpen} onOpenChange={setScannerOpen} />

        <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
          <img
            src={BRAND_LOGO}
            alt="TA Fitness Logo"
            className="h-8 sm:h-9 object-contain"
          />
        </Link>

        <div className="flex items-center">
          <div className="flex items-center gap-2 sm:gap-2.5">
            {cfg && (
              <span className={`hidden md:inline text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.className}`}>
                {cfg.label}
              </span>
            )}

            {/* Active workout timer pill rendered alongside avatar, NEVER replacing it */}
            {activeWorkout && (
              <WorkoutTimerPill size="sm" />
            )}

            {/* Account Avatar Button — ALWAYS VISIBLE */}
            <button
              type="button"
              onClick={handleAccountClick}
              className="flex items-center gap-2 group cursor-pointer focus:outline-none"
              data-testid="topbar-account-button"
              aria-label="Athlete Profile"
            >
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/20 border-2 border-primary/40 group-hover:border-primary flex items-center justify-center text-primary font-bold text-xs sm:text-sm transition-colors">
                {initials}
              </div>
            </button>
          </div>
        </div>
      </div>
      <AthleteProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
}